import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCrow, faDove, faFeather, faUserTie } from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';

import { EPackages, EPackageTiers } from '@feathr/blackbox';
import { cssVar } from '@feathr/hooks';

export const packageLicenseToLabelMap = (t: TFunction, packageLicense: EPackages): string => {
  const map: Record<EPackages, string> = {
    // Finch
    [EPackages.Finch]: t('Finch'),
    [EPackages.FinchLicense]: t('Finch License'),
    [EPackages.FinchLicenseExtended]: t('Finch License - Extended'),
    [EPackages.FinchLegacy]: t('Finch Legacy'),
    [EPackages.FinchLegacyLicense]: t('Finch Legacy License'),

    // Phoenix
    [EPackages.Phoenix]: t('Phoenix'),
    [EPackages.PhoenixLicense]: t('Phoenix License'),
    [EPackages.PhoenixLicenseExtended]: t('Phoenix License - Extended'),

    // Falcon
    [EPackages.Falcon]: t('Falcon'),
    [EPackages.FalconLicense]: t('Falcon License'),
    [EPackages.FalconLicenseExtended]: t('Falcon License - Extended'),

    // Enterprise
    [EPackages.EnterpriseFlamingo]: t('Enterprise Flamingo'),
    [EPackages.EnterpriseMarketingSeat]: t('Enterprise Marketing Seat'),
    [EPackages.EnterpriseUnlimitedMarketingSeats]: t('Enterprise Unlimited Marketing Seats'),
    [EPackages.EnterpriseMonetizationSeat]: t('Enterprise Monetization Seat'),
    [EPackages.EnterpriseUnlimitedMonetizationSeats]: t('Enterprise Unlimited Monetization Seats'),
    [EPackages.EnterpriseFalcon]: t('Enterprise Falcon'),
    [EPackages.EnterpriseFalconSeat]: t('Enterprise Falcon Seat'),
    [EPackages.EnterpriseFalconUnlimitedSeats]: t('Enterprise Falcon Unlimited Seats'),

    // Feathr Platform
    [EPackages.FeathrPlatformLite]: t('Feathr Platform - Lite'),
    [EPackages.FeathrPlatformBase]: t('Feathr Platform - Base'),
    [EPackages.FeathrPlatformStarter]: t('Feathr Platform - Starter'),
    [EPackages.FeathrPlatformEssential]: t('Feathr Platform - Essential'),
    [EPackages.FeathrPlatformEssentialPro]: t('Feathr Platform - Essential Pro'),
    [EPackages.FeathrPlatformAdvanced]: t('Feathr Platform - Advanced'),
    [EPackages.FeathrPlatformAdvancedPro]: t('Feathr Platform - Advanced Pro'),
    [EPackages.FeathrPlatformEnterprise]: t('Feathr Platform - Enterprise'),
    [EPackages.FeathrPlatformEnterprisePro]: t('Feathr Platform - Enterprise Pro'),

    // Internal
    [EPackages.Internal]: t('Internal'),
  };

  return map[packageLicense] ?? t('Invalid license');
};

export const packageTiersToLabelMap = (t: TFunction, packageTier?: EPackageTiers): string => {
  const map: Record<EPackageTiers, string> = {
    [EPackageTiers.EnterpriseFalcon]: t('Enterprise Falcon'),
    [EPackageTiers.Finch]: t('Finch'),
    [EPackageTiers.Falcon]: t('Falcon'),
    [EPackageTiers.Phoenix]: t('Phoenix'),
    [EPackageTiers.Lite]: t('Lite'),
    [EPackageTiers.Base]: t('Base'),
    [EPackageTiers.Starter]: t('Starter'),
    [EPackageTiers.Essential]: t('Essential'),
    [EPackageTiers.Advanced]: t('Advanced'),
    [EPackageTiers.Enterprise]: t('Enterprise'),
  };

  return packageTier ? map[packageTier] : t('None');
};
export const packageLicenseTierToLabelMap = (t: TFunction, packageLicense: EPackages): string => {
  const map: Partial<Record<EPackages, string>> = {
    // Finch Tier
    [EPackages.Finch]: packageTiersToLabelMap(t, EPackageTiers.Finch),
    [EPackages.FinchLicenseExtended]: packageTiersToLabelMap(t, EPackageTiers.Finch),
    [EPackages.FinchLegacyLicense]: packageTiersToLabelMap(t, EPackageTiers.Finch),
    [EPackages.FinchLicense]: packageTiersToLabelMap(t, EPackageTiers.Finch),

    // Falcon Tier
    [EPackages.Falcon]: packageTiersToLabelMap(t, EPackageTiers.Falcon),
    [EPackages.FalconLicense]: packageTiersToLabelMap(t, EPackageTiers.Falcon),
    [EPackages.EnterpriseFalconSeat]: packageTiersToLabelMap(t, EPackageTiers.Falcon),
    [EPackages.EnterpriseFalconUnlimitedSeats]: packageTiersToLabelMap(t, EPackageTiers.Falcon),

    // Phoenix Tier
    [EPackages.Phoenix]: packageTiersToLabelMap(t, EPackageTiers.Phoenix),
    [EPackages.PhoenixLicense]: packageTiersToLabelMap(t, EPackageTiers.Phoenix),
    [EPackages.PhoenixLicenseExtended]: packageTiersToLabelMap(t, EPackageTiers.Phoenix),

    // Platform Tier
    [EPackages.FeathrPlatformBase]: packageTiersToLabelMap(t, EPackageTiers.Base),
    [EPackages.FeathrPlatformStarter]: packageTiersToLabelMap(t, EPackageTiers.Starter),
    [EPackages.FeathrPlatformEssential]: packageTiersToLabelMap(t, EPackageTiers.Essential),
    [EPackages.FeathrPlatformEssentialPro]: packageTiersToLabelMap(t, EPackageTiers.Essential),
    [EPackages.FeathrPlatformAdvanced]: packageTiersToLabelMap(t, EPackageTiers.Advanced),
    [EPackages.FeathrPlatformAdvancedPro]: packageTiersToLabelMap(t, EPackageTiers.Advanced),
    [EPackages.FeathrPlatformEnterprise]: packageTiersToLabelMap(t, EPackageTiers.Enterprise),
    [EPackages.FeathrPlatformEnterprisePro]: packageTiersToLabelMap(t, EPackageTiers.Enterprise),
    [EPackages.FeathrPlatformLite]: packageTiersToLabelMap(t, EPackageTiers.Lite),
  };

  // if the packageLicense is not in the map, return the label
  return map[packageLicense] ?? packageLicenseToLabelMap(t, packageLicense);
};

export const packageLicenseToIconMap = (packageLicense?: EPackages): IconDefinition => {
  const map: Record<EPackages, IconDefinition> = {
    // Finch
    [EPackages.Finch]: faDove,
    [EPackages.FinchLicense]: faDove,
    [EPackages.FinchLicenseExtended]: faDove,
    [EPackages.FinchLegacy]: faDove,
    [EPackages.FinchLegacyLicense]: faDove,

    // Phoenix
    [EPackages.Phoenix]: faFeather,
    [EPackages.PhoenixLicense]: faFeather,
    [EPackages.PhoenixLicenseExtended]: faFeather,

    // Falcon
    [EPackages.Falcon]: faCrow,
    [EPackages.FalconLicense]: faCrow,
    [EPackages.FalconLicenseExtended]: faCrow,

    // Flamingo
    [EPackages.EnterpriseFlamingo]: faCrow,

    // Enterprise
    [EPackages.EnterpriseMarketingSeat]: faFeather,
    [EPackages.EnterpriseUnlimitedMarketingSeats]: faFeather,
    [EPackages.EnterpriseMonetizationSeat]: faFeather,
    [EPackages.EnterpriseUnlimitedMonetizationSeats]: faFeather,

    // Enterprise Falcon
    [EPackages.EnterpriseFalcon]: faFeather,
    [EPackages.EnterpriseFalconSeat]: faFeather,
    [EPackages.EnterpriseFalconUnlimitedSeats]: faFeather,

    // Feathr Platform
    [EPackages.FeathrPlatformLite]: faFeather,
    [EPackages.FeathrPlatformBase]: faFeather,
    [EPackages.FeathrPlatformStarter]: faFeather,
    [EPackages.FeathrPlatformEssential]: faFeather,
    [EPackages.FeathrPlatformEssentialPro]: faFeather,
    [EPackages.FeathrPlatformAdvanced]: faFeather,
    [EPackages.FeathrPlatformAdvancedPro]: faFeather,
    [EPackages.FeathrPlatformEnterprise]: faFeather,
    [EPackages.FeathrPlatformEnterprisePro]: faFeather,

    // Internal
    [EPackages.Internal]: faUserTie,
  };
  return packageLicense ? map[packageLicense] : faFeather;
};

export const packageLicenseColorMap = (packageLicense?: EPackages): string => {
  const map: Record<EPackages, string> = {
    // Finch
    [EPackages.Finch]: cssVar('--color-primary-3'),
    [EPackages.FinchLicense]: cssVar('--color-primary-3'),
    [EPackages.FinchLicenseExtended]: cssVar('--color-primary-3'),
    [EPackages.FinchLegacy]: cssVar('--color-primary-3'),
    [EPackages.FinchLegacyLicense]: cssVar('--color-primary-3'),

    // Phoenix
    [EPackages.Phoenix]: cssVar('--color-brand-yellow-dark'),
    [EPackages.PhoenixLicense]: cssVar('--color-brand-yellow-dark'),
    [EPackages.PhoenixLicenseExtended]: cssVar('--color-brand-yellow-dark'),

    // Falcon
    [EPackages.Falcon]: cssVar('--color-brand-purple'),
    [EPackages.FalconLicense]: cssVar('--color-brand-purple'),
    [EPackages.FalconLicenseExtended]: cssVar('--color-brand-purple'),

    // Flamingo
    [EPackages.EnterpriseFlamingo]: cssVar('--color-brand-purple-light'),

    // Enterprise
    [EPackages.EnterpriseMarketingSeat]: cssVar('--color-brand-orange'),
    [EPackages.EnterpriseUnlimitedMarketingSeats]: cssVar('--color-brand-orange'),
    [EPackages.EnterpriseMonetizationSeat]: cssVar('--color-brand-orange'),
    [EPackages.EnterpriseUnlimitedMonetizationSeats]: cssVar('--color-brand-orange'),

    // Enterprise Falcon
    [EPackages.EnterpriseFalcon]: cssVar('--color-brand-orange'),
    [EPackages.EnterpriseFalconSeat]: cssVar('--color-brand-orange'),
    [EPackages.EnterpriseFalconUnlimitedSeats]: cssVar('--color-brand-orange'),

    // Feathr Platform
    [EPackages.FeathrPlatformLite]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformBase]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformStarter]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformEssential]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformEssentialPro]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformAdvanced]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformAdvancedPro]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformEnterprise]: cssVar('--color-brand-yellow-dark'),
    [EPackages.FeathrPlatformEnterprisePro]: cssVar('--color-brand-yellow-dark'),

    // Internal
    [EPackages.Internal]: cssVar('--color-danger'),
  };
  return packageLicense ? map[packageLicense] : cssVar('--color-primary-3');
};
